import { z } from 'zod';
import { Cents, PlainDate } from './common.js';
import { QuestionKind } from './acting-for.js';
import { AwardTypeCode, PoolKind, PoolPosition } from './awards.js';
import { FeedbackChannel } from './feedback.js';
import { SpotValue } from './spot-value.js';

const Ref = z.object({ id: z.string(), name: z.string() });

/**
 * APP-232: the Spot nomination a Waiting on you row is about, with only what the reader's own Spot
 * view already shows: never the nominator or the write-up. APP-240: the value is null when the
 * nominator chose none (Q9), and `title` is the nominator's Title, which the committee, the manager
 * and the Stock Admin team all read (null on a nomination sent before titles).
 */
const SpotNominationRef = z
  .object({
    id: z.uuid(),
    value: SpotValue.nullable(),
    title: z.string().nullable().optional(),
    sentAt: z.iso.datetime({ offset: true }),
  })
  .strict();

/** The award a waiting item is about, with only what the row needs. */
const AwardRef = z.object({
  id: z.uuid(),
  displayId: z.string().nullable(),
  type: AwardTypeCode,
  title: z.string().nullable(),
  people: z.number().int(),
  cashCents: Cents,
  options: z.number().int(),
  sector: Ref.nullable(),
  program: Ref.nullable(),
  enteredBy: Ref,
  /** A delegate's entry: the Sector Lead it was entered for. */
  enteredFor: Ref.nullable(),
  /**
   * Who it's for: the first three people on it, in entry order (`people` counts them all), so a
   * row can say "for Dana Park, J. Moore and S. Quinn". Optional: older readers omit it.
   */
  recipients: z.array(Ref).max(3).optional(),
});

/**
 * APP-138: the one report with replies to read, when there's only one ("Read 1 reply on
 * FB-2026-0014", "Daniel Cho answered by email · ‹title›"): its latest unread reply's channel and
 * time, and how many replies on it are unread ("Read 2 replies on FB-2026-0014").
 */
const FeedbackRepliesOne = z
  .object({
    displayId: z.string(),
    title: z.string(),
    /** The sender's name. */
    from: z.string(),
    channel: FeedbackChannel,
    at: z.string(),
    replies: z.number().int().positive(),
  })
  .strict();

const base = {
  id: z.string(),
  /** When it started waiting; the list shows the oldest first (CLAUDE.md "Waiting on you"). */
  since: z.string(),
};

/**
 * Everything waiting on a person, from every role they hold (HOME1, HOME2, E1). Typed facts, not
 * copy: the web words them, so the bell and the Home cards say the same thing.
 */
export const WaitingItem = z.discriminatedUnion('type', [
  z.object({
    ...base,
    type: z.literal('payroll_queue'),
    role: z.literal('payroll'),
    toPay: z.int().nonnegative(),
    statementsDue: z.int().nonnegative(),
  }),
  z.object({
    ...base,
    type: z.literal('make_election'),
    role: z.literal('employee'),
    lineId: z.uuid(),
    version: z.int().positive(),
    reward: z
      .object({
        id: z.uuid(),
        displayId: z.string().nullable(),
        type: z.literal('PAA'),
        title: z.string().nullable(),
        awardDate: PlainDate,
      })
      .strict(),
    deadline: z.object({ date: PlainDate, at: z.iso.datetime({ offset: true }) }).strict(),
  }),
  z.object({
    ...base,
    type: z.literal('approve_delegate_entry'),
    role: z.enum(['sector_lead', 'executive']),
    award: AwardRef,
    withinBudget: z.boolean(),
  }),
  z.object({
    ...base,
    type: z.literal('executive_decision'),
    role: z.literal('executive'),
    award: AwardRef,
  }),
  z.object({
    ...base,
    type: z.literal('approve_overage'),
    role: z.literal('executive'),
    award: AwardRef,
    /**
     * The pool it's over and by how much. Both null when a budget added since sending now covers
     * it: the award still waits for an Executive's decision (APP-037).
     */
    pool: Ref.nullable(),
    shortfall: z.object({ cashCents: Cents, options: z.number().int() }).nullable(),
  }),
  z.object({
    ...base,
    type: z.literal('record_decision'),
    role: z.enum(['requester', 'executive_delegate', 'stock_admin']),
    award: AwardRef,
  }),
  z.object({
    ...base,
    type: z.literal('returned_to_you'),
    role: z.enum(['sector_lead', 'delegate', 'executive_delegate', 'requester', 'executive']),
    award: AwardRef,
    reason: z.string().nullable(),
  }),
  z.object({
    ...base,
    /**
     * APP-148 D17: an award held by "Not what you asked?", until it's settled. The System Admins
     * and the Stock Admin team have it as theirs; so does whoever settles it: any Executive for
     * what was done for an Executive, the award's Sector Lead for their own. `since` is when it
     * was questioned; held awards come before the rest of the live work.
     */
    type: z.literal('acted_for_held'),
    role: z.enum(['stock_admin', 'system_admin', 'executive', 'sector_lead']),
    actedForId: z.uuid(),
    award: AwardRef,
    questionedBy: Ref,
    kind: QuestionKind,
  }),
  z.object({
    ...base,
    /**
     * APP-245 D7: letters released to this person to hand out and not handed out yet, counted by
     * letter from Letters to hand out's own scope and summary (`handoutTotals`), so the numbers
     * always match. `since` is when the oldest of them was released. `role` is the role they
     * hold them as (D7a [default]): Sector Lead or Delegate, then Manager, else Employee.
     */
    type: z.literal('letters_to_hand_out'),
    role: z.enum(['sector_lead', 'delegate', 'manager', 'employee']),
    count: z.number().int().positive(),
  }),
  z.object({
    ...base,
    type: z.literal('stock_admin_queue'),
    role: z.literal('stock_admin'),
    count: z.number().int(),
  }),
  /** PAA people whose tax from ADP is still to enter, oldest approval first (APP-101). */
  z.object({
    ...base,
    type: z.literal('stock_admin_tax'),
    role: z.literal('stock_admin'),
    count: z.number().int(),
    awards: z.number().int(),
  }),
  z.object({
    ...base,
    type: z.literal('set_budgets'),
    /** The Executives (X1), or Finance (F1), who may also set budgets. */
    role: z.enum(['executive', 'finance']),
    fiscalYear: z.number().int(),
    /** Every pool with no budget for the year, with the cash already counting against it. */
    pools: z.array(z.object({ pool: Ref, kind: PoolKind, usedCashCents: Cents })),
    /** No pool has a budget yet: the year's first budgets. */
    allPools: z.boolean(),
  }),
  z.object({
    ...base,
    type: z.literal('getting_started'),
    role: z.literal('employee'),
    step: z.enum(['preferences', 'training']),
  }),
  z.object({
    ...base,
    /**
     * A System Admin's work (SYS1, QA-040): claims idle past the timeout, active people
     * missing sector, home org or OH/G&A, directory discrepancies (an app value differing from
     * Entra, a likely duplicate), and the go-live steps still required before launch.
     */
    type: z.literal('system_admin_task'),
    role: z.literal('system_admin'),
    task: z.enum([
      'stuck_claims',
      'needs_setup',
      'discrepancies',
      'go_live',
      'feedback_to_review',
      'feedback_replies_to_read',
    ]),
    /** For `feedback_replies_to_read`, the number of reports; for the rest, the things to do. */
    count: z.number().int().positive(),
    /**
     * Every role the row comes from, when more than one: new feedback for someone who is both a
     * System Admin and Support is one row, tagged with both (APP-104 §6). Absent otherwise.
     */
    roles: z
      .array(z.enum(['system_admin', 'support']))
      .min(2)
      .refine((roles) => new Set(roles).size === roles.length, 'duplicate_role')
      .optional(),
    /** `feedback_replies_to_read` on one report only: that report. Absent otherwise. */
    one: FeedbackRepliesOne.optional(),
  }),
  z.object({
    ...base,
    /**
     * APP-232: a sent Spot nomination with colleagues waiting for the committee's decision, for a
     * Spot Committee member (whoever reviews Spot nominations today). `nominees` names the first
     * three waiting, in the nomination's order; `waiting` counts them all. `since` is when it was
     * sent. It opens the committee's review of the nomination.
     */
    type: z.literal('spot_committee_review'),
    role: z.literal('spot_reviewer'),
    nomination: SpotNominationRef,
    nominees: z.array(Ref).min(1).max(3),
    waiting: z.number().int().positive(),
  }),
  z.object({
    ...base,
    /**
     * APP-232: a colleague whose Spot award waits for this person's decision as their supervisor
     * (the directory manager, or a standing appointed supervisor): the committee's recommendation
     * and award date. `since` is the committee's recommendation. It opens the decision. APP-243
     * (change 13): nothing about the pool or its funding, which never holds the decision.
     */
    type: z.literal('spot_supervisor_decision'),
    role: z.literal('manager'),
    nomination: SpotNominationRef,
    nominee: Ref,
    cashCents: Cents,
    awardDate: PlainDate,
  }),
  z.object({
    ...base,
    /**
     * APP-232: a colleague whose Spot award waits on a supervisor nobody can be, because no manager
     * is set for them, for the Stock Admin team to appoint one. `since` is the committee's
     * recommendation. It opens the appoint form for them on Spot awards, at No manager set (APP-235).
     */
    type: z.literal('spot_supervisor_needed'),
    role: z.literal('stock_admin'),
    nomination: SpotNominationRef,
    nominee: Ref,
  }),
  z.object({
    ...base,
    /**
     * APP-104: new feedback to review; `since` is the oldest new report's date. APP-138: replies
     * to read, `count` reports; `since` is the oldest unread reply's date.
     */
    type: z.literal('support_task'),
    role: z.literal('support'),
    task: z.enum(['feedback_to_review', 'feedback_replies_to_read']),
    count: z.number().int().positive(),
    /** `feedback_replies_to_read` on one report only: that report. Absent otherwise. */
    one: FeedbackRepliesOne.optional(),
  }),
]);
export type WaitingItem = z.infer<typeof WaitingItem>;

export const Waiting = z.object({
  asOf: PlainDate,
  items: z.array(WaitingItem),
  /**
   * An Executive's approval requests also reach the Executive group address now (APP-092): it's
   * set and their emails are on. Only for Executives; Home's note says so only when it's true.
   */
  executiveGroupEmail: z.boolean().optional(),
});
export type Waiting = z.infer<typeof Waiting>;

/** A staff contact: name and job title only, never other fields (APP-054). */
export const StaffContact = z.object({
  id: z.uuid(),
  name: z.string(),
  /** From the directory; null when it has none. */
  title: z.string().nullable(),
});
export type StaffContact = z.infer<typeof StaffContact>;

/**
 * R1: what a request's card says beyond the award list (APP-054): who it's for, when it was sent
 * and who the one summary notice went to, then how it ended: letters released, closed, and the
 * final summary to the Executives. Only for the reader's own requests.
 */
export const RequestFacts = z.object({
  award: z.object({ id: z.uuid() }),
  /** The first three people on it, in entry order (`people` counts them all). */
  recipients: z.array(Ref).max(3),
  people: z.number().int().nonnegative(),
  /** When it was sent; null for a draft. */
  sentAt: z.string().nullable(),
  /** Who the summary notice went to, as recorded when it went; empty until it has gone. */
  noticeTo: z.array(Ref),
  /** The first letter released; null until then. */
  lettersReleasedAt: z.string().nullable(),
  closedAt: z.string().nullable(),
  /** The final summary (people, amounts and budget impact) has gone to the Executives. */
  finalSummarySent: z.boolean(),
});
export type RequestFacts = z.infer<typeof RequestFacts>;

/**
 * `GET /api/v1/home/requester`: R1's side cards, for Executives and pre-approved requesters only
 * (APP-054). Narrower than Budget pools: the Executive pool's position this year and nothing else.
 */
export const HomeRequester = z.object({
  /** This year's Executive pool; null while previewing (as Budget pools) or with no such pool. */
  executivePool: PoolPosition.nullable(),
  /** The active Stock Admin team, by name, for "Need a hand?". */
  stockAdmins: z.array(StaffContact),
  /**
   * The reader's own Executive Discretionary Bonus requests, newest first (up to 50), for the My
   * requests cards. Empty while previewing. Optional: older servers omit it.
   */
  requests: z.array(RequestFacts).optional(),
});
export type HomeRequester = z.infer<typeof HomeRequester>;
